// The embed's side of the portal-products host contract: every app-specific value the products
// model, the product shell, and the legacy redirects need, assembled once and handed down.
//
// Mounting the provider IS the wiring — it owns the per-customer product fetch (the access
// snapshot plus the local inputs) and publishes the result through the package's own store, which
// `Routes.tsx` reads to gate each product's pages.
//
// The embed's session is a hash, not a login: there is no permission store and no user profile
// row, so the viewer's grants and permission facts are module constants (see `embedViewer.ts`,
// decisions A1/A6) and `isAdminViewer` is false (A2).

import React from 'react';
import { toast } from 'react-toastify';
import {
  PortalHostProvider,
  SALES_MAILTO,
  createApolloConfigServicePort,
  createApolloProductFetchers,
} from '@hopdrive/feature-portal-products';
import { ProductShellExperience } from '@hopdrive/feature-product-shell';
import { SettingsPanel } from '@hopdrive/feature-settings-ui';
import { createConfigServiceAdapter } from '@hopdrive/feature-settings-ui/adapter';
import type {
  PortalHostComponents,
  PortalHostNotify,
  PortalHostRouting,
  PortalHostValue,
} from '@hopdrive/feature-portal-products';

import client from '@apollolocal/client';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import { useRooftop, useUser } from '@store';
import { actorIdentity } from '@utils/actorIdentity';

import { EMBED_GRANTED_FEATURES, EMBED_PRODUCT_PERMISSIONS } from './embedViewer';
import { isFullBleedPath } from './isFullBleedPath';
import { PRODUCT_ROUTES } from './productRoutes';
import { REACT_ROUTER_PORT } from './reactRouterPort';

// Bound once at module load: the client is a singleton, and every one of these objects is
// referenced by the memoized host value, so a per-render identity would re-run the fetch effect.
const FETCHERS = createApolloProductFetchers(client);
const SETTINGS_REQUEST = createApolloConfigServicePort(client);
// The embed has no invoices, billing, or user-management routes, so no semantic action type
// resolves to an in-app route; every one of them opens the sales mailto instead.
const ROUTING: PortalHostRouting = { isFullBleedPath, actionRoutes: {}, routes: PRODUCT_ROUTES, salesMailto: SALES_MAILTO };
const COMPONENTS: PortalHostComponents = {
  Fallback: DefaultFallback,
  ProductShell: ProductShellExperience,
  SettingsPanel,
};
const NOTIFY: PortalHostNotify = {
  info: message => toast.info(message, { autoClose: 2000, toastId: 'refetch' }),
  error: message => toast.error(message, { toastId: 'refetch-error' }),
};

interface EmbedPortalHostProps {
  children: React.ReactNode;
}

const EmbedPortalHost: React.FC<EmbedPortalHostProps> = ({ children }) => {
  const user = useUser();
  const rooftop = useRooftop();

  // The working customer, in the order the embed learns it: the hash session's claim, the rooftop
  // the hash pinned, then the loaded rooftop record.
  const customerId = user?.claims?.['x-hasura-customer-id'] ?? user?.rooftopId ?? rooftop?.id;
  const role = user?.claims?.['x-hasura-default-role'] ?? (user ? 'dealer' : null);
  // One attribution string for every embed write (decision A8, superseding A3's `dealer-<id>`):
  // the same `embed:rooftop:<id>` the feature code already writes through `actorIdentity`. Read
  // outside the memo so the dependency is the string, not the user object's identity.
  const actorLabel = actorIdentity(user);

  const host = React.useMemo<PortalHostValue>(
    () => ({
      viewer: {
        role,
        isAdminViewer: false,
        customerId,
        // No `viewerId`: the hash session identifies a rooftop, not a user row, and the
        // persisted product snapshot is keyed by user AND customer precisely so one browser
        // cannot serve another person's menu. Omitting it is the package's documented default
        // — the fetch is unchanged, nothing is read from or written to storage.
        actorLabel,
        grantedFeatures: EMBED_GRANTED_FEATURES,
        permissionsLoaded: true,
        productPermissions: EMBED_PRODUCT_PERMISSIONS,
      },
      fetchers: FETCHERS,
      settings: { request: SETTINGS_REQUEST, createAdapter: createConfigServiceAdapter },
      routing: ROUTING,
      router: REACT_ROUTER_PORT,
      components: COMPONENTS,
      notify: NOTIFY,
    }),
    [role, customerId, actorLabel],
  );

  return <PortalHostProvider value={host}>{children}</PortalHostProvider>;
};

export default EmbedPortalHost;
